import { LockedNote, useFullAccess } from "../../billing/access";
import { t } from "../../i18n";
import { tEngine } from "../../i18n/engine";
import { useEffect, useMemo, type ReactNode } from "react";
import { computeQuantities, costEstimate, fmt, makeDefaultPricing, type QuantitiesResult } from "@tasduvar/engine";
import { useProjectStore, useSelectedWall } from "../../state/projectStore";
import { useAnalysis } from "../../state/useAnalysis";
import { useCadUi } from "../../state/cadUiStore";
import { useProvider } from "../../provider/useProvider";
import { CadFormContext } from "../../ui/formContext";
import { QuantitiesTab } from "../wall-editor/QuantitiesTab";
import { WallList } from "../project/WallList";
import { PricingTable, completePricing } from "../project/PricingDialog";
import { useQuantitiesExport } from "../results/QuantitiesSummary";
import { Palette } from "./Palette";
import { CommandLine } from "./CommandLine";
import { LayoutTabs } from "./LayoutTabs";

/** Beyaz panel: 24 px başlık bandı (10/600/BÜYÜK) + sağda mono bilgi + gövde (README §3 "Metraj"). */
function Panel({ title, info, children, label }: { title: string; info?: ReactNode; children: ReactNode; label: string }): JSX.Element {
  return (
    <section aria-label={label} className="bg-white border border-cad-line min-w-0">
      <div className="cad-head">{title}{info ? <span className="ml-auto font-mono font-normal normal-case tracking-normal text-cad-ink2">{info}</span> : null}</div>
      {children}
    </section>
  );
}

const TH = "px-2 py-0.5 font-semibold text-left";
const TD = "px-2 py-0.5";
const NUM = "font-mono tabular-nums text-right";

/** Metraj tablosu: İmalat · Birim · 1 m için · Toplam · Hesap (bilgi satırları gri, keşfe girmez) */
function QuantityTable({ q }: { q: QuantitiesResult }): JSX.Element {
  return (
    <table className="w-full text-[11px] border-collapse" aria-label={t("Metraj tablosu")}>
      <thead><tr className="bg-cad-head text-cad-ink2"><th className={TH}>{t("İmalat")}</th><th className={TH}>{t("Birim")}</th><th className={`${TH} text-right`}>{t("1 m için")}</th><th className={`${TH} text-right`}>{t("Toplam")}</th><th className={TH}>{t("Hesap")}</th></tr></thead>
      <tbody>
        {q.lines.map((l) => (
          <tr key={l.key + l.label} className={`border-b border-cad-soft2 ${l.info ? "text-cad-ink3 italic" : ""}`} aria-label={tEngine(l.label)}>
            <td className={TD}>{tEngine(l.label)}{l.info ? <span className="ml-1 not-italic text-[10px]">({t("bilgi")})</span> : null}</td>
            <td className={`${TD} font-mono`}>{l.unit === "adet" ? t("adet") : l.unit}</td>
            <td className={`${TD} ${NUM}`}>{fmt(l.perMetre, l.unit === "kg" ? 1 : l.unit === "adet" ? 0 : 2)}</td>
            <td className={`${TD} ${NUM} font-medium`}>{fmt(l.total, l.unit === "kg" ? 1 : l.unit === "adet" ? 0 : 2)}</td>
            <td className={`${TD} font-mono text-[10px] text-cad-ink3`}>{tEngine(l.formula)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Donatı metrajı: poz bazında (Ø, adet/m, boy, kg/m, toplam) ve çap bazında özet */
function RebarTables({ rebar }: { rebar: NonNullable<QuantitiesResult["rebar"]> }): JSX.Element {
  return (
    <div className="grid gap-2 p-2 border-t border-cad-soft2">
      <table className="w-full text-[11px] border-collapse" aria-label={t("Donatı metrajı (poz)")}>
        <thead><tr className="bg-cad-head text-cad-ink2"><th className={TH}>{t("Poz")}</th><th className={`${TH} text-right`}>Ø</th><th className={`${TH} text-right`}>{t("adet/m")}</th><th className={`${TH} text-right`}>{t("Boy")}</th><th className={`${TH} text-right`}>kg/m</th><th className={`${TH} text-right`}>{t("Toplam")} kg</th><th className={TH}>{t("Not")}</th></tr></thead>
        <tbody>
          {rebar.byPosition.map((r) => (
            <tr key={r.position + r.dia} className="border-b border-cad-soft2">
              <td className={TD}>{tEngine(r.label)}</td><td className={`${TD} ${NUM}`}>{r.dia}</td><td className={`${TD} ${NUM}`}>{fmt(r.countPerMetre, 2)}</td><td className={`${TD} ${NUM}`}>{fmt(r.length, 2)}</td>
              <td className={`${TD} ${NUM}`}>{fmt(r.kgPerMetre, 1)}</td><td className={`${TD} ${NUM} font-medium`}>{fmt(r.total, 1)}</td><td className={`${TD} text-[10px] text-cad-ink3`}>{tEngine(r.note)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-cad-ink2">
        {rebar.byDiameter.map((d) => <span key={d.dia}>Ø{d.dia}: {fmt(d.kgPerMetre, 1)} kg/m · {fmt(d.total, 0)} kg</span>)}
        <span className="font-semibold">Σ {fmt(rebar.kgPerMetre, 1)} kg/m · {fmt(rebar.total, 0)} kg</span>
      </div>
    </div>
  );
}

/**
 * Metraj paftası (README §3 "Metraj", §7 faz 4): sol METRAJ GİRDİLERİ paleti (özellik ızgarası) + XLSX/CSV; ortada
 * "Metraj — 1 m ve toplam" tablosu (+ donatı) ve düzenlenebilir POZ VE BİRİM FİYAT TABLOSU (Miktar/Tutar, toplam);
 * sağda DUVARLAR listesi; altta layout sekmeleri + komut satırı. Sayılar `computeQuantities` / `costEstimate`'ten.
 */
export function QuantitiesLayout(): JSX.Element {
  const wall = useSelectedWall();
  const analysis = useAnalysis(wall);
  const { project, updateWall, updatePricing, readOnly } = useProjectStore();
  const cad = useCadUi();
  const providerPricing = useProvider((s) => s.settings.defaults.pricing);
  const r = analysis.result;
  const q = useMemo(() => (wall && r ? computeQuantities(wall, r) : null), [wall, r]);
  const pricing = useMemo(() => completePricing(project?.pricing), [project?.pricing]);
  const cost = useMemo(() => (q ? costEstimate([q], pricing) : null), [q, pricing]);
  const exp = useQuantitiesExport(wall!, q ?? { structureType: "masonry-gravity", length: 0, lines: [], rebar: null, assumptions: [] });
  const full = useFullAccess();
  // Şerit Metraj › XLSX / CSV isteği
  const request = useCadUi((s) => s.exportRequest), clearRequest = useCadUi((s) => s.setExportRequest);
  useEffect(() => {
    if (request !== "xlsx" && request !== "csv") return;
    clearRequest(null);
    if (!q || !full) return;
    if (request === "xlsx") void exp.xlsx(); else exp.csv();
  }, [request]);
  if (!wall || !project) return <p className="p-6 text-cad-ink3">{t("Duvar seçin.")}</p>;
  const title = `${t("Metraj girdileri")} — ${wall.name}`;
  return (
    <>
      {readOnly ? null : (
        <Palette title={title} id="cad-quantities-inputs" className="shrink-0 border-r border-cad-line" style={{ width: cad.palettes.left || 268 }}
          footer={t("Çıktılar: şerit › Metraj › XLSX / CSV")}>
          <CadFormContext.Provider value={true}>
            <div className="cad-props h-full"><div className="cad-props-body grid" role="group" aria-label={t("Metraj girdileri")}><QuantitiesTab wall={wall} update={(recipe) => updateWall(wall.id, recipe)} inline /></div></div>
          </CadFormContext.Provider>
        </Palette>
      )}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <div className="flex-1 overflow-auto bg-cad-off p-3 grid gap-3 content-start min-w-0" aria-label={t("Metraj paftası")}>
          {analysis.error ? <p role="alert" className="border border-[#B42318]/50 bg-cad-badbg px-2 py-1.5 text-[11px] text-[#B42318]">{t("Hesap yapılamadı:")} {analysis.error}</p> : null}
          {!full ? <LockedNote what={t("Metraj ve keşif (miktarlar, donatı, tutarlar)")} /> : null}
          {q && full ? (
            <Panel title={t("Metraj — 1 m ve toplam")} label={t("Metraj özeti")} info={<>L = {fmt(q.length, 1)} m{q.rebar ? ` · ${t("donatı")} ${fmt(q.rebar.total, 0)} kg` : ""}</>}>
              {q.assumptions.length ? <p className="px-2 py-1 text-[11px] text-cad-ink3 border-b border-cad-soft2">{q.assumptions.map((a) => tEngine(a)).join(" · ")}</p> : null}
              <QuantityTable q={q} />
              {q.rebar ? <RebarTables rebar={q.rebar} /> : null}
            </Panel>
          ) : null}
          <Panel title={t("Poz ve birim fiyat tablosu")} label={t("Poz ve birim fiyat tablosu")} info={<>{pricing.year ? `${t("fiyat yılı")}: ${pricing.year}` : `${t("fiyat yılı")}: ${t("girilmedi")}`} · {pricing.currency} · {t("KDV hariç")}</>}>
            <CadFormContext.Provider value={true}>
              <div className="cad-props"><div className="cad-props-body grid">
                <PricingTable cad value={pricing} onChange={readOnly ? () => undefined : updatePricing} label={t("Poz ve birim fiyat tablosu")} cost={cost ?? undefined} />
                {!readOnly ? <button type="button" className="cad-btn-box justify-self-start" onClick={() => updatePricing(providerPricing ? structuredClone(providerPricing) : makeDefaultPricing())}>{t("Sağlayıcı varsayılanlarına dön")}</button> : null}
                {cost && !cost.priced ? <p className="text-[11px] text-cad-ink3">{t("Fiyatı boş bırakılan poz keşif toplamına girmez → keşif özeti (rapor 13b) şimdilik hesaplanamıyor.")}</p> : null}
              </div></div>
            </CadFormContext.Provider>
          </Panel>
        </div>
        <LayoutTabs current="quantities" />
        <CommandLine disabled={readOnly} />
      </div>
      {cad.palettes.right !== null ? (
        <aside className="shrink-0 flex flex-col min-h-0 border-l border-cad-line bg-cad-panel" style={{ width: cad.palettes.right }}>
          <div className="flex-1 min-h-0 flex flex-col"><WallList variant="cad" /></div>
        </aside>
      ) : null}
    </>
  );
}
